import { useRef, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { z } from "zod";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import GridBackdrop from "@/components/GridBackdrop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Info, Loader2, Clock, Users, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackSubmission } from "@/lib/analytics";
import { format } from "date-fns";

const timeSlots = [
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
];

const meetingSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, { message: "Please enter your name." })
    .max(100, { message: "Name must be less than 100 characters." }),
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email address." })
    .max(255, { message: "Email must be less than 255 characters." }),
  phone: z
    .string()
    .trim()
    .min(1, { message: "Please enter your phone number." })
    .max(30, { message: "Phone must be less than 30 characters." }),
  company: z
    .string()
    .trim()
    .min(1, { message: "Please enter your company name." })
    .max(100, { message: "Company must be less than 100 characters." }),
  topic: z
    .string()
    .trim()
    .min(10, { message: "Please describe your topic (10+ characters)." })
    .max(1000, { message: "Topic must be less than 1000 characters." }),
});

const Meeting = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [date, setDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionPending = useRef(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    topic: "",
    honeypot: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionPending.current) return;
    setIsSubmitting(true);
    setFormError(null);
    setFieldErrors({});

    const parsed = meetingSchema.safeParse({
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      topic: formData.topic,
    });

    const errs: Record<string, string> = {};

    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !errs[key]) errs[key] = issue.message;
      }
    }

    if (!date) {
      errs.date = "Please select a date.";
    }

    if (!selectedTime) {
      errs.time = "Please select a time.";
    }

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setFormError("Please fix the highlighted fields and try again.");
      setIsSubmitting(false);
      return;
    }

    submissionPending.current = true;
    try {
      if (!date) throw new Error("Meeting date is required");
      const formattedDate = format(date, "MMMM d, yyyy");

      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          type: "meeting",
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          date: formattedDate,
          time: selectedTime,
          topic: formData.topic,
          honeypot: formData.honeypot,
        },
      });

      if (error) throw error;
      if (data?.success !== true) throw new Error("Submission was not accepted");

      trackSubmission("meeting", data);
      navigate("/thank-you/meeting");
    } catch (error) {
      console.error("Error sending email:", error);
      const errorMessage = "Please try again or call us directly.";
      setFormError(errorMessage);
      toast({
        title: "Something went wrong",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      submissionPending.current = false;
      setIsSubmitting(false);
    }
  };

  // Disable weekends and past dates
  const disabledDays = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = date.getDay();
    return day === 0 || day === 6 || date < today;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Schedule a Consultation"
        description="Book a consultation with NETIUS to discuss your web development project. Central Florida-based team serving clients nationwide. Available Monday-Friday."
        path="/meeting"
      />
      <Header />

      <main className="flex-1">
        {/* Aria-live region for form errors */}
        <div aria-live="polite" className="sr-only">
          {formError && `Error: ${formError}`}
        </div>

        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <GridBackdrop />
          <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
                Request a Meeting
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Request a consultation with our team. We&apos;re available Monday through Friday,
                9:00 AM to 12:00 PM EST.
              </p>
              <div className="inline-flex items-center gap-2 mt-6 px-3 py-1.5 rounded-full bg-secondary/80 border border-border text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                Please book at least 24 hours in advance.
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3 justify-center text-sm text-muted-foreground mt-8">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>30-minute call</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <span>Free consultation</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  <span>No obligation</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Meeting Form */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8 bg-secondary/50 border border-border ring-1 ring-white/[0.08] dark:ring-white/[0.12] rounded-lg p-4 flex items-start gap-3">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Please note: Your meeting request is not guaranteed until confirmed by a member of our
                  team. We will review your request and do our best to accommodate your preferred time slot.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="bg-secondary/50 border border-border ring-1 ring-white/[0.08] dark:ring-white/[0.12] rounded-lg p-6 md:p-8" noValidate>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        placeholder="John Doe"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        disabled={isSubmitting}
                        aria-required="true"
                        aria-invalid={!!fieldErrors.fullName}
                        aria-describedby={fieldErrors.fullName ? "fullName-error" : undefined}
                      />
                      {fieldErrors.fullName && (
                        <p id="fullName-error" className="text-xs text-destructive mt-1">{fieldErrors.fullName}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        disabled={isSubmitting}
                        aria-required="true"
                        aria-invalid={!!fieldErrors.email}
                        aria-describedby={fieldErrors.email ? "email-error" : undefined}
                      />
                      {fieldErrors.email && (
                        <p id="email-error" className="text-xs text-destructive mt-1">{fieldErrors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        placeholder="+1 (555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        disabled={isSubmitting}
                        aria-required="true"
                        aria-invalid={!!fieldErrors.phone}
                        aria-describedby={fieldErrors.phone ? "phone-error" : undefined}
                      />
                      {fieldErrors.phone && (
                        <p id="phone-error" className="text-xs text-destructive mt-1">{fieldErrors.phone}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="company">Company Name *</Label>
                      <Input
                        id="company"
                        placeholder="Acme Inc."
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        disabled={isSubmitting}
                        aria-required="true"
                        aria-invalid={!!fieldErrors.company}
                        aria-describedby={fieldErrors.company ? "company-error" : undefined}
                      />
                      {fieldErrors.company && (
                        <p id="company-error" className="text-xs text-destructive mt-1">{fieldErrors.company}</p>
                      )}
                    </div>
                  </div>

                  {/* Honeypot field - hidden from users, visible to bots */}
                  <div className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
                    <Label htmlFor="company_url">Company URL</Label>
                    <Input
                      id="company_url"
                      name="company_url"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.honeypot}
                      onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="topic">Meeting Topic *</Label>
                    <Textarea
                      id="topic"
                      placeholder="Please describe what you'd like to discuss..."
                      rows={4}
                      value={formData.topic}
                      onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                      disabled={isSubmitting}
                      aria-required="true"
                      aria-invalid={!!fieldErrors.topic}
                      aria-describedby={fieldErrors.topic ? "topic-error" : undefined}
                    />
                    {fieldErrors.topic && (
                      <p id="topic-error" className="text-xs text-destructive mt-1">{fieldErrors.topic}</p>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <Label className="mb-3 block">Select Date *</Label>
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={disabledDays}
                        className="rounded-md border bg-background"
                      />
                      {fieldErrors.date && (
                        <p className="text-xs text-destructive mt-2">{fieldErrors.date}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Available Monday - Friday, 24 hours in advance
                      </p>
                    </div>
                    <div>
                      <Label className="mb-3 block">Select Time *</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {timeSlots.map((time) => (
                          <Button
                            key={time}
                            type="button"
                            variant={selectedTime === time ? "default" : "outline"}
                            className="w-full"
                            onClick={() => setSelectedTime(time)}
                            disabled={isSubmitting}
                          >
                            {time}
                          </Button>
                        ))}
                      </div>
                      {fieldErrors.time && (
                        <p className="text-xs text-destructive mt-2">{fieldErrors.time}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-4">
                        30-minute slots (EST)
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    By submitting this form, you agree to our{" "}
                    <a href="/privacy" className="underline">Privacy Policy</a> and{" "}
                    <a href="/terms" className="underline">Terms of Service</a>.
                  </p>

                  <Button type="submit" className="w-full" size="lg" disabled={isSubmitting} aria-busy={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        SUBMITTING...
                      </>
                    ) : (
                      "REQUEST MEETING"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Meeting;
